import { useLocale } from "@/store/locale-store";
import {
  getDefaultLanguageCode,
  getLanguageName,
  getSupportedLanguageCodes,
  LanguageCode,
} from "@/utils/LocaleUtil";
import CheckIcon from "@/v2-components/core/icon/check-icon";
import ChevronLeftIcon from "@/v2-components/core/icon/chevron-left-icon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/v2-components/ui/accordion";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/v2-components/ui/sheet";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import { cn } from "@/libs/utils";
import { useShippingCountries } from "@/hooks/shops/useCountries";
import CountryFlag from "@/components/CountryFlag";
import { useGeo } from "@/hooks/useGeo";
import ConfirmCountry from "@/components/ConfirmCountry";
import { ShippingCountry } from "@/libs/getShippingCountries";

interface Props {
  onPrevious: () => void;
}

export function LocaleView({ onPrevious }: Props) {
  const { languageCode, countryCode, setLocale } = useLocale();
  const [value, setValue] = useState<string | undefined>(undefined);
  const [state, setState] = useState({
    countryCode,
    languageCode,
  });
  const { data } = useGeo();
  const [showAlert, setShowAlert] = useState(false);

  return (
    <Fragment>
      {showAlert && (
        <ConfirmCountry
          languageCode={state.languageCode}
          onCancel={() => {
            setShowAlert(false);
          }}
          onConfirm={async () => {
            setLocale(state);
            setShowAlert(false);
          }}
          onClose={() => {
            setShowAlert(false);
          }}
        />
      )}
      {/* ★**형제 뷰(`menu-view.tsx`)와 같은 처방이다** (#5576).

          `SheetHeader` 기본이 `-mt-4 laptop:-mt-8` 인데 그건 `sheetVariants` 베이스
          `p-4 laptop:p-8` 을 상쇄하는 값이다. 이 시트는 `p-0 laptop:p-0` 이라 상쇄할 대상이
          없어 **헤더가 위로 잘려 올라갔다**(실측: `top -16`). `menu-view.tsx:162` 가 같은
          이유로 이미 `mt-0 … laptop:mt-0` 을 명시하는데 이 뷰만 맨 `<SheetHeader>` 였다 —
          **한 시트의 두 뷰가 갈린 자리**다.

          `px-4` 도 같이 준다. 없으면 `<` 가 좌 0 에 앉아 아래 아코디언 라벨(16)과 16 어긋난다.

          ⚠️ **프리미티브를 고치지 않는다** — `SheetHeader` 소비처가 18곳이고 나머지는 시트
          베이스 패딩(`p-4`)을 그대로 쓰므로 `-mt-4` 가 거기서는 맞는 값이다. */}
      <SheetHeader className="mt-0 items-center px-4 py-5 laptop:mt-0 laptop:py-5">
        {/* 탭 타겟을 44 로 넓히고 `-m-2.5` 로 되돌려 **아이콘 위치는 그대로** 둔다 —
            형제 뷰의 닫기 버튼(`menu-view.tsx:169`)과 같은 표기다. 이 `<` 는 로케일
            뷰에서 돌아가는 유일한 수단이라 24×24 로 두면 iOS 44 최소치에 못 미친다. */}
        <button className="-m-2.5 p-2.5" onClick={onPrevious}>
          <ChevronLeftIcon />
        </button>
        <SheetTitle className="sr-only">Shipping & Language</SheetTitle>
        <SheetDescription className="sr-only">
          Select your shipping country and language
        </SheetDescription>
      </SheetHeader>
      {/* ★**음수 마진을 두지 않는다** (#5576).

          종전엔 `-mx-4` 였다 — 시트(`SheetContent`)의 베이스 패딩 `p-4` 를 상쇄해 국가 목록을
          전폭으로 쓰려던 것이다. 그런데 리뉴얼에서 시트가 `p-0 laptop:p-0` 로 바뀌어
          (`navigation-menu-sheet/index.tsx`, 종전 `w-[90%] … pr-4`) **상쇄할 대상이 사라졌다.**
          그래서 나가는 쪽만 남아 아코디언 트리거가 화면 끝에 붙었다:

            nav  -16  +  AccordionTrigger 의 `px-4`  +16  =  좌우 여백 **0**

          실측(2026-09-07 dev 390px): 라벨 좌여백 0 · 셰브론 우여백 0.
          `-mx-4` 를 걷으면 트리거가 자기 `px-4` 로 16 을 갖고, 국가 목록은 `pl-7`(28)을
          그대로 유지한다 — 둘 다 시트가 `p-4` 였던 시절과 같은 값이다. */}
      <nav className="flex-1 overflow-hidden">
        <Accordion
          type="single"
          collapsible
          className="flex h-full flex-col"
          value={value}
          onValueChange={setValue}
        >
          <AccordionItem
            value={"Shipping country/region"}
            className="flex flex-col border-none data-[state=open]:grow data-[state=open]:overflow-hidden"
          >
            <AccordionTrigger
              size="lg"
              className="font-400 data-[state=open]:font-600"
            >
              <span className="relative">{"Shipping country/region"}</span>
              <ChevronDownIcon />
            </AccordionTrigger>
            <AccordionContent className="mt-4 h-full flex-1 overflow-hidden px-0 py-0">
              <Countries
                countryCode={countryCode}
                onSelectCountry={async (countryCode) => {
                  // 국가가 변경이 되면 언어도 기본 언어로 변경됩니다.
                  const languageCode = getDefaultLanguageCode(countryCode);
                  if (
                    !!data &&
                    data.shippingSupported &&
                    data.countryCode !== countryCode
                  ) {
                    setState((prev) => ({
                      ...prev,
                      countryCode,
                      languageCode,
                    }));
                    setShowAlert(true);
                    return;
                  }
                  setLocale({
                    countryCode,
                    languageCode,
                  });
                }}
              />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value={"Language"}
            className="border-none data-[state=open]:grow data-[state=open]:overflow-hidden"
          >
            <AccordionTrigger
              size="lg"
              className="font-400 data-[state=open]:font-600"
            >
              <span className="relative">{"Language"}</span>
              <ChevronDownIcon />
            </AccordionTrigger>
            <AccordionContent className="mt-0 px-0 py-0">
              <Languages
                countryCode={countryCode}
                languageCode={languageCode}
                onSelectLanguage={async (languageCode) => {
                  setLocale({
                    countryCode,
                    languageCode,
                  });
                }}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </nav>
    </Fragment>
  );
}

const Languages = ({
  onSelectLanguage,
  languageCode,
  countryCode,
}: {
  countryCode: string;
  onSelectLanguage: (languageCode: LanguageCode) => Promise<void>;
  languageCode: LanguageCode;
}) => {
  const supportedLanguageCodes = getSupportedLanguageCodes(countryCode);
  const length = supportedLanguageCodes.length;

  return (
    <ul className="flex w-full flex-col">
      {supportedLanguageCodes.map((_languageCode, index) => (
        <li key={_languageCode} className="px-7 py-5 pr-4 hover:bg-coolgray-10">
          <button
            className={cn(
              "relative flex w-full items-center justify-between",
              length <= 1 && "pointer-events-none",
            )}
            onClick={async () => {
              await onSelectLanguage(_languageCode);
            }}
          >
            <p
              className={cn(
                "text-base",
                _languageCode === languageCode && "font-600",
              )}
            >
              {getLanguageName(_languageCode)}
            </p>
            {_languageCode === languageCode && (
              <CheckIcon className="absolute right-0" />
            )}
          </button>
        </li>
      ))}
    </ul>
  );
};

const INDEX_KEYS = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""), "#"];

const Countries = ({
  onSelectCountry,
  countryCode,
}: {
  countryCode: string;
  onSelectCountry: (countryCode: string) => Promise<void>;
}) => {
  const { data, isPending } = useShippingCountries();
  const isScrubbingRef = useRef(false);
  const lastKeyRef = useRef<string | null>(null);

  const allCountries = useMemo(
    () =>
      Object.values(data ?? {})
        .flat()
        .sort((a, b) =>
          a.englishCountryName.localeCompare(b.englishCountryName),
        ),
    [data],
  );

  const grouped = allCountries.reduce<Record<string, ShippingCountry[]>>(
    (acc, c) => {
      const key = (c.englishCountryName?.[0] ?? "#").toUpperCase();
      (acc[key] ??= []).push(c);
      return acc;
    },
    {},
  );

  const groupKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  const scrollToKey = (key: string) => {
    if (lastKeyRef.current === key) {
      return;
    }
    lastKeyRef.current = key;

    const el = document.getElementById(`country-group-${key}`);
    el?.scrollIntoView({ block: "start" });
  };

  const handleScrubAtPoint = (clientX: number, clientY: number) => {
    const target = document.elementFromPoint(clientX, clientY);
    const keyEl = target?.closest?.("[data-index-key]") as
      HTMLElement | null | undefined;
    const key = keyEl?.dataset?.indexKey;

    if (key) {
      scrollToKey(key);
    }
  };

  return (
    <div className="relative flex h-full w-full">
      <div className="flex-1 overflow-auto">
        <ul className="flex w-full flex-col">
          {groupKeys.map((key) => (
            <li key={key} className="mb-4">
              <div
                id={`country-group-${key}`}
                className="px-1 pl-7 text-xs font-500 text-coolgray-60"
              >
                {key}
              </div>

              {grouped[key].map((c) => {
                const selected = c.countryCode === countryCode;

                return (
                  <button
                    key={c.countryCode}
                    type="button"
                    className="w-full cursor-pointer pl-7 text-left"
                    onClick={async () => {
                      await onSelectCountry(c.countryCode);
                    }}
                  >
                    <CountryRow
                      className="border-b"
                      countryCode={c.countryCode}
                      countryName={c.ownCountryName}
                      selected={selected}
                    />
                  </button>
                );
              })}
            </li>
          ))}
        </ul>
      </div>
      <div className="">
        <ul
          className="relative top-7 mr-1 flex touch-none flex-col rounded-full bg-coolgray-10 py-1"
          onPointerDown={(e) => {
            isScrubbingRef.current = true;
            handleScrubAtPoint(e.clientX, e.clientY);
          }}
          onPointerMove={(e) => {
            if (!isScrubbingRef.current) {
              return;
            }
            handleScrubAtPoint(e.clientX, e.clientY);
          }}
          onPointerUp={() => {
            isScrubbingRef.current = false;
            lastKeyRef.current = null;
          }}
          onPointerCancel={() => {
            isScrubbingRef.current = false;
            lastKeyRef.current = null;
          }}
          onPointerLeave={() => {
            isScrubbingRef.current = false;
            lastKeyRef.current = null;
          }}
          onTouchStart={(e) => {
            const t = e.touches.item(0);
            if (!t) {
              return;
            }
            isScrubbingRef.current = true;
            handleScrubAtPoint(t.clientX, t.clientY);
          }}
          onTouchMove={(e) => {
            if (!isScrubbingRef.current) {
              return;
            }
            const t = e.touches.item(0);
            if (!t) {
              return;
            }
            handleScrubAtPoint(t.clientX, t.clientY);
          }}
          onTouchEnd={() => {
            isScrubbingRef.current = false;
            lastKeyRef.current = null;
          }}
          onTouchCancel={() => {
            isScrubbingRef.current = false;
            lastKeyRef.current = null;
          }}
        >
          {INDEX_KEYS.map((k) => {
            const enabled = Boolean(grouped[k]?.length);

            if (!enabled) {
              return null;
            }

            return (
              <li key={k} className="text-center">
                <button
                  type="button"
                  data-index-key={k}
                  disabled={!enabled}
                  className={cn(
                    "rounded px-2 text-center text-[10px] font-600 leading-[14px]",
                    enabled ? "text-coolgray-60" : "text-coolgray-30",
                  )}
                  onClick={() => {
                    scrollToKey(k);
                  }}
                >
                  {k}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const CountryRow = ({
  countryCode,
  countryName,
  className,
  selected,
}: {
  countryCode: string;
  countryName: string;
  className?: string;
  selected: boolean;
}) => {
  return (
    <div
      className={`flex w-full items-center justify-between border-coolgray-10 py-3.5 ${className ? "border-b-0" : "border-b"}`}
    >
      <div className="flex grow items-center gap-2.5">
        <CountryFlag countryCode={countryCode} />
        <div className="text-base text-coolgray-90">{countryName}</div>
      </div>

      <CheckSVG className={selected ? "opacity-100" : "opacity-0"} />
    </div>
  );
};

const CheckSVG = ({ className }: { className?: string }) => (
  <svg
    width="24"
    height="24"
    className={cn("size-6", className)}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 16.4L6 12.4L7.4 11L10 13.6L16.6 7L18 8.4L10 16.4Z"
      fill="#21272A"
    />
  </svg>
);
